import { Moment } from 'moment';
import { lastValueFrom, Observable, of } from 'rxjs';
import { map } from 'rxjs';

import { days } from '@shared-constants/date-constants';
import { DATE_FORMAT_FRIENDLY_WITH_WEEKDAY } from '@shared-constants/date-format-constants';
import { BasicInterfaceTypeEnum } from '@shared-enums/cats.enum';
import { TimesheetStatusEnum } from '@shared-enums/time.enum';
import { arrayIsTruthy, findUnique, isSameArray } from '@shared-helpers/array-object-helpers';
import { getUnixString } from '@shared-helpers/date-helpers';
import { safeCompare } from '@shared-helpers/string-number-helpers';
import * as sharedApi from '@shared-interfaces/api';
import { IDayOfWeek, IWeekDayNumbers } from '@shared-interfaces/dates';
import { IExtDocument } from '@shared-interfaces/documents';

import { ICatRecord } from '../interfaces/incoming';
import { ITableConfigEntity } from '../interfaces/internal-interfaces';
import { Employee } from '../scripts/employee';
import { DatahubService } from '../services/datahub.service';
import { ModalService } from '../services/modal.service';
import { BasicInterface, IAnyInterfaceType } from './basicInterface';
import { IBillingSummaryRow } from './BillingTimesheet';
import { DateHandler } from './date-handler';
import { Equipment } from './equipment';
import { ExtDocument } from './extDocument';
import { IPayrollTotals } from './PayrollTimesheet';
import { ProductionEquipment } from './productionEquipment';
import { ProductionQuantity } from './ProductionQuantity';
import { ProductionText } from './ProductionText';

export interface ITimesheetDate {
  moment: Moment;
  weekDay: IDayOfWeek;
  number: IWeekDayNumbers;
  unix: string;
  display: string;
}

export interface ITimesheetStatus {
  number: '' | TimesheetStatusEnum;
  description: string;
  shouldPermitPayrollApproval: boolean;
  shouldPermitBillingApproval: boolean;
  error: string;
}

export abstract class TimesheetHandler {
  //So im sorry that we ended up referring to client outputs or external documents as "timesheets" in the app. A timesheet for this class and its dependents is a grouping of cats records in the UI, but is not given a number. The number
  //is used for billing. This class may have been misnamed, but I can't think of a better one.
  public employees: Employee[] = []; //TODO: there should be a class for handling employee groups and equipment groups ect. Just like the contract manager class.
  public employeeRecords: ICatRecord[] = [];

  public equipment: Equipment[] = [];
  public equipmentRecords: ICatRecord[] = [];

  public productionQuantity: ProductionQuantity[] = [];
  public productionQuantityRecords: ICatRecord[] = [];

  public productionEquipment: ProductionEquipment[] = [];

  public productionText: ProductionText[] = [];
  public productionTextRecords: ICatRecord[] = [];

  public catsRecords: ICatRecord[] = [];

  public timesheetStatus: ITimesheetStatus = {
    number: '',
    description: 'No Time entered',
    shouldPermitPayrollApproval: true,
    shouldPermitBillingApproval: true,
    error: '',
  };

  public isPayroll: boolean;

  public display = '';

  public extDocuments: ExtDocument[] = [];
  public applicableExtDocuments: ExtDocument[] = []; //starting v28, there was a need for greater control over which timesheets appear in detail payne. Detail payne will use this list.
  public userExtDocuments: ExtDocument[] = [];

  public weDate: DateHandler = this.datahub.weDate;
  constructor(
    protected readonly datahub: DatahubService,
    protected readonly modalService: ModalService,
  ) {
    this.weDate.setDate(this.weDate.getWeDate());
  }

  public viewFinished(): void {
    //this fires once the view inits and asks for timesheet data.
    this.datahub.detailPayneHandler.setTimesheet(this);
    this.datahub.detailPayneHandler.showSummary();
  }

  public getWeek(): ITimesheetDate[] {
    return days.map((x) => this.getDate(x.toLowerCase() as IDayOfWeek));
  }

  public getDate(dayString: IDayOfWeek): ITimesheetDate {
    //accepts a relative date string like "fri", "sat" etc. and returns absolute information.
    const moment = this.weDate.convertDayOfWeekToMoment(dayString);
    return {
      moment: moment,
      weekDay: dayString,
      number: moment.day() as IWeekDayNumbers,
      unix: getUnixString(moment),
      display: moment.format(DATE_FORMAT_FRIENDLY_WITH_WEEKDAY),
    };
  }

  public filterByCostCenter(costCenter: string): void {
    this.filterItemByCostCenter(this.employees, costCenter);
    this.filterItemByCostCenter(this.equipment, costCenter);
    this.filterArrayItemByCostCenter(this.productionEquipment, costCenter);
    this.filterArrayItemByCostCenter(this.productionQuantity, costCenter);
    this.filterArrayItemByCostCenter(this.productionText, costCenter);
    if (costCenter) this.applicableExtDocuments = this.extDocuments.filter((x) => x.Costcenter1 === costCenter);
    else this.applicableExtDocuments = this.extDocuments;
  }

  public filterArrayItemByCostCenter(
    items: ProductionQuantity[] | ProductionText[] | ProductionEquipment[],
    costCenter: string,
  ): void {
    this.showAll(items);
    items.forEach((item: ProductionQuantity | ProductionText | ProductionEquipment) => {
      if (!costCenter) {
        item.filterByCostCenter('');
        return;
      }
      const found = item.costCenters.find((x) => safeCompare(x, costCenter));
      if (!found) {
        item.hide();
        return;
      }
      item.filterByCostCenter(costCenter);
      item.show();
    });
  }

  public showAll(items: BasicInterface[]): void {
    items.forEach((item) => {
      item.show();
    });
  }

  public determineTimesheetStatus(): TimesheetStatusEnum {
    const billableDocs: ExtDocument[] = [];
    const nonBillableDocs: ExtDocument[] = [];
    this.extDocuments.forEach((x) => {
      if (x.isBillable()) billableDocs.push(x);
      else nonBillableDocs.push(x);
    });

    //given list of cats records, set the status of the timesheet accordingly and return the number.
    this.datahub.recalcTotals();
    const domesticDocuments = this.extDocuments.filter((x) => !x.isForeign);
    if (!arrayIsTruthy(domesticDocuments)) return this.setTimesheetStatus(TimesheetStatusEnum.Created, 'No Time entered yet');
    const billableStatus = this.getTimesheetStatusFromDocuments(billableDocs);
    const nonbillOrPerDiemStatus = this.getTimesheetStatusFromDocuments(nonBillableDocs);
    return this.determineGlobalStatusFromNonbillableAndBillableStatus(nonbillOrPerDiemStatus, billableStatus);
  }

  private getTimesheetStatusFromDocuments(documents: ExtDocument[]): TimesheetStatusEnum {
    const statuses = findUnique<TimesheetStatusEnum>(documents, 'Status', true);
    if (documents.length === 0) {
      return TimesheetStatusEnum.Created; //if there are no BILLABLE documents, then CR message is fine
    } else if (statuses.some((s) => s === TimesheetStatusEnum.InProgress)) {
      return TimesheetStatusEnum.InProgress;
    } else if (statuses.some((s) => s === TimesheetStatusEnum.PayrollApproved)) {
      return TimesheetStatusEnum.PayrollApproved;
    } else if (statuses.some((s) => s === TimesheetStatusEnum.BillingApproved)) {
      return TimesheetStatusEnum.BillingApproved;
    } else if (statuses.some((s) => s === TimesheetStatusEnum.Created)) {
      return TimesheetStatusEnum.Created;
    } else {
      return TimesheetStatusEnum.Error; //otherwise we made a mistake somewhere
    }
  }

  public disableApproval(approvalStage: string, error: string): void {
    if (approvalStage === 'billing') {
      this.timesheetStatus.shouldPermitBillingApproval = false;
    } else {
      this.timesheetStatus.shouldPermitPayrollApproval = false;
    }
    this.timesheetStatus.error = error;
  }

  public sort(): void {
    this.employees = this.employees.sort((a, b) => {
      //TODO: this needs to handle per diems
      const aTotal = Number(a.total); //sort employees first by if there are hours, next by last name.
      const bTotal = Number(b.total);
      if (!!aTotal === !!bTotal) return a.LastName > b.LastName ? 1 : -1;
      if (!aTotal && bTotal) return 1;
      if (!bTotal && aTotal) return -1;
      return 0;
    });
    this.equipment = this.equipment.sort((a, b) => {
      const aTotal = Number(a.payrollTotal);
      const bTotal = Number(b.payrollTotal);
      if (!!aTotal === !!bTotal) return a.EquipmentId > b.EquipmentId ? 1 : -1;
      if (!aTotal && bTotal) return 1;
      if (!bTotal && aTotal) return -1;
      return 0;
    });
  }

  public findUserExtDocuments$(): Observable<IExtDocument[]> {
    const params = {
      Dateentered: this.weDate.getFilterString(),
      Owner: this.datahub.user.getApprover(),
    };
    return this.datahub.apiService.getTimesheets$(params);
  }

  public findExtDocuments$(): Observable<ExtDocument[] | IExtDocument[]> {
    const documents = findUnique<string>(this.catsRecords, 'Extdocumentno', false);
    const current = findUnique<string>(this.extDocuments, 'Documentno');
    if (!arrayIsTruthy(documents)) return of(this.extDocuments); //if there are no documents in cats records, or there are no cats records, nothing to do here.
    if (isSameArray(documents, current)) return of(this.extDocuments); //if we already have the same documents, no need to go lookup more.
    const params = {
      Documentno: documents,
    };
    return this.datahub.apiService.getTimesheets$(params).pipe(
      map((values) => {
        values.forEach((document: IExtDocument) => {
          if (!this.extDocuments.some((x) => x.Documentno === document.Documentno)) {
            this.extDocuments.push(new ExtDocument(document, this.datahub, false));
          }
        });
        return this.extDocuments;
      }),
    );
  }

  public addExtDoc(document: ExtDocument): void {
    const foundDoc = this.extDocuments.find((x) => x.Documentno === document.Documentno);
    if (!foundDoc) {
      this.extDocuments.push(document);
    }
  }

  public assignEmployeeRecords(timeEvalResultsArr: sharedApi.IPayrollTimeEvalResult[] = []): void {
    let timeEvalEmployees = timeEvalResultsArr;
    if (timeEvalEmployees.length > 0) {
      //if time eval is being run, only run time eval when employees have results from time eval.
      timeEvalEmployees = timeEvalResultsArr.filter((x) => arrayIsTruthy(x.TimeEvalResultSet?.results));
    }

    //find cats records in search belonging to Employee. Make records into array by each unique employee number

    this.employees.forEach((employee) => {
      const records = this.employeeRecords.filter((record) => safeCompare(employee.EmpId, record.Employeenumber));
      employee.setCatsRecords(records); //basic interface classes each have the ability to perform separate required logic by type.
      if (timeEvalEmployees.length > 0) {
        const timeEvalResult = timeEvalEmployees.find((x) => safeCompare(x.Pernr, employee.EmpId));
        if (timeEvalResult) {
          employee.compareTimeEval(timeEvalResult);
        } else {
          //This usually happens for non-direct employees, they won't be included in the time eval results, but if they still show the loading tooltip we need to remove it
          const timeEvalConfig = employee.payrollConfig.find((x) => x.key === 'total');
          if (timeEvalConfig?.tooltip === this.datahub.payrollTimesheet.timeEvalLoadingMessage) {
            timeEvalConfig.tooltip = '';
          }
        }
      }
    });
  }

  public assignEquipmentRecords(): void {
    //find cats records in search belonging to equipment. Make records into array by each unique equipment number
    this.equipment.forEach((equipment) => {
      const records = this.equipmentRecords.filter((record) => safeCompare(equipment.EquipmentId, record.Zequipment));
      equipment.setCatsRecords(records); //basic interface classes each have the ability to perform separate required logic by type.
    });
  }

  public async approve(isPayroll: boolean): Promise<void> {
    const domesticDocuments = this.extDocuments.filter((x) => !x.isForeign);
    const shouldApprove = await this.modalService.launchConfirmModal({
      title: 'Confirm that all data is correct',
      yesText: 'Approve',
      noText: 'Cancel',
    });
    if (!shouldApprove) return;
    this.timesheetStatus.shouldPermitBillingApproval = false;
    this.timesheetStatus.shouldPermitPayrollApproval = false;

    try {
      await lastValueFrom(this.datahub.catsDB.approve$(domesticDocuments));
      const status = isPayroll ? TimesheetStatusEnum.PayrollApproved : TimesheetStatusEnum.BillingApproved;
      this.updateExtDocumentStatusAfterApproval(status);

      const modalConfig = isPayroll
        ? {
            title: 'Payroll records have been approved',
            body: 'Proceed to the home page to approve billing',
            buttonText: 'Home Page',
          }
        : {
            title: 'Billing records have been approved',
            body: 'Timesheets have been sent to billing',
            buttonText: 'Home Page',
          };

      const shouldNav = await lastValueFrom(this.modalService.launchResponsiveModal('simple-text', modalConfig).afterClosed());
      if (shouldNav) {
        this.datahub.navigate('/home');
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error in approve:', error);
      this.permitApprovals();
    } finally {
      this.permitApprovals();
    }
  }

  public setApplicableDocumentsToAllDocuments(): void {
    this.applicableExtDocuments = this.extDocuments;
  }

  private filterItemByCostCenter(items: (Employee | Equipment)[], costCenter: string): void {
    this.showAll(items);
    const losers = items.filter((item) => item.CostCenter !== costCenter);
    losers.forEach((item) => {
      item.hide();
    });
  }

  private determineGlobalStatusFromNonbillableAndBillableStatus(
    nonbillStatus: TimesheetStatusEnum,
    billableStatus: TimesheetStatusEnum,
  ): TimesheetStatusEnum {
    if (billableStatus === TimesheetStatusEnum.Error) {
      return this.setTimesheetStatus(TimesheetStatusEnum.Error, 'An Error Occurred Determining Timesheet Status');
    } else if (nonbillStatus === TimesheetStatusEnum.Created && billableStatus === TimesheetStatusEnum.Created) {
      return this.setTimesheetStatus(TimesheetStatusEnum.Created, 'No Time entered yet');
    } else if (nonbillStatus === TimesheetStatusEnum.InProgress || billableStatus === TimesheetStatusEnum.InProgress) {
      return this.setTimesheetStatus(TimesheetStatusEnum.InProgress, 'Needs Payroll Approval');
    } else if (billableStatus === TimesheetStatusEnum.PayrollApproved) {
      return this.setTimesheetStatus(TimesheetStatusEnum.PayrollApproved, 'Needs Billing approval');
    } else {
      return this.setTimesheetStatus(TimesheetStatusEnum.BillingApproved, 'All time has been approved');
    }
  }

  private setTimesheetStatus(status: TimesheetStatusEnum, description: string): TimesheetStatusEnum {
    this.timesheetStatus.number = status;
    this.timesheetStatus.description = description;
    this.timesheetStatus.shouldPermitBillingApproval = true;
    this.timesheetStatus.shouldPermitPayrollApproval = true;
    this.timesheetStatus.error = '';
    // this.timesheetStatus.permitBillingApproval = status !== TimesheetStatusEnum.BillingApproved || !status;
    // this.timesheetStatus.permitPayrollApproval = status == TimesheetStatusEnum.InProgress;
    return status;
  }

  private permitApprovals(): void {
    this.timesheetStatus.shouldPermitBillingApproval = true;
    this.timesheetStatus.shouldPermitPayrollApproval = true;
  }

  private updateExtDocumentStatusAfterApproval(status: TimesheetStatusEnum): void {
    //update each timesheet object after an approval if necessary.
    const domesticDocuments = this.extDocuments.filter((x) => !x.isForeign);
    domesticDocuments.forEach((document) => {
      if (document.Status === TimesheetStatusEnum.Created) return; //if the timesheet is not touched, we don't upgrade it here
      if (document.Status === status) return; //don't bother updating a timesheet to the status it already is
      if (document.Status === TimesheetStatusEnum.BillingApproved && status === TimesheetStatusEnum.PayrollApproved) return; //Don't downgrade a timesheet when approving
      document.updateSelf(status);
    });
  }

  //Methods which classes that extend this class must implement
  public abstract addDocument(entity: IAnyInterfaceType): void;
  public abstract updateExtDocumentStatus(extDocumentsNo: string[], billCodeType: BasicInterfaceTypeEnum): void;
  public abstract getConfig(): ITableConfigEntity[];
  public abstract getValues(): Array<IBillingSummaryRow | IPayrollTotals>;
}
